import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Shield, Crown, ChevronRight, Plus, Search, Trophy, Mail, ArrowLeft, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';

export default function ManageAdminsPage({ user, setUser }) {
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [adminsRes, usersRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/admin/league-admins`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/admin/leagues`, { headers })
      ]);
      setAdmins(adminsRes.data);
      setAllUsers(usersRes.data.filter(u => u.role !== 'admin')); // Non-admin users
      setLeagues(leaguesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAdminLeagues = (adminId) => {
    return leagues.filter(l => l.admin_id === adminId);
  };

  const promoteToLeagueAdmin = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${API}/users/${selectedUser.user_id}/role`, {
        role: 'admin',
        admin_type: 'league_admin'
      }, { headers });
      
      setPromoteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Error promoting user:', error);
      alert(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const assignLeagueToAdmin = async () => {
    if (!selectedLeague || !selectedAdmin) return;
    
    try {
      await axios.put(`${API}/leagues/${selectedLeague}/assign-admin`, {
        admin_id: selectedAdmin
      }, { headers });
      
      setAssignDialogOpen(false);
      setSelectedLeague(null);
      setSelectedAdmin(null);
      fetchData();
    } catch (error) {
      console.error('Error assigning league:', error);
      alert(error.response?.data?.detail || 'Failed to assign league');
    }
  };

  const demoteAdmin = async (adminId) => {
    if (!confirm('Are you sure you want to remove admin privileges from this user?')) return;
    
    try {
      await axios.put(`${API}/users/${adminId}/role`, {
        role: 'viewer',
        admin_type: null
      }, { headers });
      fetchData();
    } catch (error) {
      console.error('Error demoting admin:', error);
      alert(error.response?.data?.detail || 'Failed to demote admin');
    }
  };

  const filteredAdmins = admins.filter(admin =>
    admin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if current user is super admin
  if (user?.admin_type !== 'super_admin') {
    return (
      <div className="min-h-screen bg-slate-50">
        <AdminSidebar user={user} setUser={setUser} />
        <div className="lg:ml-64 pt-14 lg:pt-0">
          <div className="p-6">
            <div className="text-center py-20">
              <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-bold text-slate-800">Access Denied</h2>
              <p className="text-slate-500 mt-2">Only Super Admins can access this page</p>
              <Link to="/admin">
                <Button className="mt-4">Back to Dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">Manage League Admins</h1>
                <Badge className="bg-[#8B1E3F] text-white border-none">
                  <Crown className="w-3 h-3 mr-1" /> Super Admin Only
                </Badge>
              </div>
              <p className="text-slate-500 mt-1">Promote users to League Admin and assign leagues</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setPromoteDialogOpen(true)}
                className="bg-[#2D5A5A] hover:bg-[#1E4545]"
              >
                <Plus className="w-4 h-4 mr-2" /> Promote User
              </Button>
              <Button 
                onClick={() => setAssignDialogOpen(true)}
                variant="outline"
                className="border-[#2D5A5A] text-[#2D5A5A]"
              >
                <Trophy className="w-4 h-4 mr-2" /> Assign League
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search admins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Admins Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#2D5A5A] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">No League Admins</h3>
              <p className="text-slate-500 mt-1">Promote users to create League Admins</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAdmins.map((admin) => {
                const adminLeagues = getAdminLeagues(admin.user_id);
                return (
                  <Card key={admin.user_id} className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader className="bg-gradient-to-r from-[#2D5A5A] to-[#3D6A6A] text-white pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                            <Shield className="w-6 h-6" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{admin.name}</CardTitle>
                            <p className="text-white/70 text-sm flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {admin.email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="mb-4">
                        <p className="text-sm font-medium text-slate-600 mb-2">Assigned Leagues ({adminLeagues.length})</p>
                        {adminLeagues.length > 0 ? (
                          <div className="space-y-2">
                            {adminLeagues.map(league => (
                              <div key={league.league_id} className="flex items-center gap-2 text-sm">
                                <Trophy className="w-4 h-4 text-[#8B1E3F]" />
                                <span>{league.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400">No leagues assigned</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => demoteAdmin(admin.user_id)}
                        >
                          Remove Admin
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* All Leagues Section */}
          <div className="mt-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4">All Leagues & Owners</h2>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 text-sm text-slate-600">
                  <tr>
                    <th className="text-left py-3 px-4">League</th>
                    <th className="text-left py-3 px-4">Location</th>
                    <th className="text-left py-3 px-4">Owner</th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {leagues.map(league => {
                    const owner = [...admins, user].find(a => a.user_id === league.admin_id);
                    return (
                      <tr key={league.league_id} className="hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-[#8B1E3F]" />
                            <span className="font-medium">{league.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{league.location}</td>
                        <td className="py-3 px-4">
                          {owner ? (
                            <div className="flex items-center gap-2">
                              <span>{owner.name}</span>
                              {owner.admin_type === 'super_admin' && (
                                <Badge className="bg-[#8B1E3F]/10 text-[#8B1E3F] border-none text-xs">Super</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">Unassigned</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedLeague(league.league_id);
                              setAssignDialogOpen(true);
                            }}
                          >
                            Reassign
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Promote User Dialog */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote User to League Admin</DialogTitle>
            <DialogDescription>
              Select a user to promote to League Admin. They will be able to create and manage their own leagues.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select onValueChange={(value) => setSelectedUser(allUsers.find(u => u.user_id === value))}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                {allUsers.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name} ({u.email}) - {u.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={promoteToLeagueAdmin}
              disabled={!selectedUser}
              className="bg-[#2D5A5A] hover:bg-[#1E4545]"
            >
              Promote to League Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign League Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign League to Admin</DialogTitle>
            <DialogDescription>
              Select a league and an admin to assign ownership.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">League</label>
              <Select value={selectedLeague || ''} onValueChange={setSelectedLeague}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a league" />
                </SelectTrigger>
                <SelectContent>
                  {leagues.map(l => (
                    <SelectItem key={l.league_id} value={l.league_id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Assign to Admin</label>
              <Select onValueChange={setSelectedAdmin}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an admin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user.user_id}>
                    {user.name} (Super Admin - You)
                  </SelectItem>
                  {admins.map(a => (
                    <SelectItem key={a.user_id} value={a.user_id}>
                      {a.name} (League Admin)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={assignLeagueToAdmin}
              disabled={!selectedLeague || !selectedAdmin}
              className="bg-[#2D5A5A] hover:bg-[#1E4545]"
            >
              Assign League
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
